<script setup lang="ts">
import type { ProviderErrorInfo } from '../../../utils/provider-error'

import { computed, ref, useId } from 'vue'

import Alert from '../../misc/Alert.vue'

export type ProviderStatus
  = | { kind: 'idle' }
    | { kind: 'validating' }
    | { kind: 'valid' }
    | { kind: 'invalid', error: ProviderErrorInfo }

const props = defineProps<{
  status: ProviderStatus
}>()

const detailOpen = ref(false)
const detailId = `provider-error-detail-${useId()}`

const error = computed<ProviderErrorInfo | null>(() => {
  return props.status.kind === 'invalid' ? props.status.error : null
})
</script>

<template>
  <Alert v-if="status.kind === 'validating'" type="loading">
    <template #title>
      正在验证配置…
    </template>
  </Alert>

  <Alert v-else-if="status.kind === 'valid'" type="success">
    <template #title>
      配置验证成功，可在下方选择模型
    </template>
  </Alert>

  <Alert v-else-if="status.kind === 'invalid' && error" type="error">
    <template #title>
      {{ error.summary }}
    </template>
    <template v-if="error.detail || error.status || error.code" #content>
      <button
        type="button"
        class="flex items-center gap-1 text-xs text-red-600 dark:text-red-300 hover:underline"
        :aria-expanded="detailOpen"
        :aria-controls="detailId"
        @click="detailOpen = !detailOpen"
      >
        <div :class="detailOpen ? 'i-solar:alt-arrow-down-bold-duotone' : 'i-solar:alt-arrow-right-bold-duotone'" />
        <span>{{ detailOpen ? '收起详细信息' : '展开详细信息' }}</span>
      </button>
      <div v-show="detailOpen" :id="detailId" class="mt-2 flex flex-col gap-1">
        <div v-if="error.status || error.code" class="flex flex-wrap gap-2 text-xs">
          <span v-if="error.status" class="rounded bg-red-100 px-2 py-0.5 text-red-700 font-mono dark:bg-red-900/40 dark:text-red-200">
            HTTP {{ error.status }}
          </span>
          <span v-if="error.code" class="rounded bg-red-100 px-2 py-0.5 text-red-700 font-mono dark:bg-red-900/40 dark:text-red-200">
            {{ error.code }}
          </span>
        </div>
        <pre v-if="error.detail" class="max-h-64 overflow-auto whitespace-pre-wrap break-all rounded bg-red-100/60 p-2 text-xs text-red-800 dark:bg-red-950/40 dark:text-red-200">{{ error.detail }}</pre>
      </div>
    </template>
  </Alert>
</template>
